import { useEffect, useRef, useState } from 'react'

export interface AudioData {
  frequencies: Float32Array
  bass: number
  mid: number
  treble: number
  overall: number
}

export function useAudioAnalyzer(audioRef: React.RefObject<HTMLAudioElement | null>, mute:boolean = false): AudioData {
  const [audioData, setAudioData] = useState<AudioData>({
    frequencies: new Float32Array(256),
    bass: 0,
    mid: 0,
    treble: 0,
    overall: 0
  })
  const audioContextRef = useRef<AudioContext | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const sourceRef = useRef<MediaElementAudioSourceNode | null>(null)
  const animationFrameRef = useRef<number | null>(null)
  const isSetupRef = useRef<boolean>(false)
  const previousSrcRef = useRef<string>('')
  const gainRef = useRef<GainNode | null>(null)

  useEffect(() => {
    // Only set up if audio element exists
    if (!audioRef?.current) return

    const audioElement = audioRef.current
    const currentSrc = audioElement.src

    // Reset setup if source changed
    if (previousSrcRef.current && previousSrcRef.current !== currentSrc) {
      isSetupRef.current = false
      // Clean up old context if it exists
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        try {
          audioContextRef.current.close()
        } catch (e) {
          // Ignore errors when closing
        }
      }
      audioContextRef.current = null
      analyserRef.current = null
      sourceRef.current = null
    }
    previousSrcRef.current = currentSrc

    // Set crossOrigin attribute for CORS (required for Web Audio API)
    // Only set if audio hasn't started loading to avoid interfering with playback
    // Note: Setting crossOrigin after src is set may cause CORS warnings but shouldn't block playback
    if (!audioElement.crossOrigin && audioElement.readyState === 0) {
      audioElement.crossOrigin = 'anonymous'
    } else if (!audioElement.crossOrigin) {
      // Audio already loading/loaded - set crossOrigin but it may not work for CORS
      // This is safe and won't prevent playback
      audioElement.crossOrigin = 'anonymous'
    }

    const setupAudio = async (): Promise<void> => {
      // Don't interfere with playback - if setup fails, just skip analyzer
      try {
        if (isSetupRef.current) return

        // Ensure crossOrigin is set (may cause CORS warnings if set after src, but won't prevent playback)
        if (!audioElement.crossOrigin) {
          audioElement.crossOrigin = 'anonymous'
        }

        // Create audio context
        const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext
        const audioContext = new AudioContextClass()
        audioContextRef.current = audioContext

        // Resume audio context if suspended (required by browsers)
        // Use try-catch to ensure this doesn't block
        try {
          if (audioContext.state === 'suspended') {
            await audioContext.resume()
          }
        } catch (resumeError) {
          console.warn('Could not resume audio context:', resumeError)
          // Continue anyway - audio should still play
        }

        // Create analyser
        const analyser = audioContext.createAnalyser()
        analyser.fftSize = 512 // Higher resolution
        analyser.smoothingTimeConstant = 0.8
        analyserRef.current = analyser

        // Connect audio source
        // Note: If CORS is not properly configured, this will output zeros but won't prevent playback
        // The CORS warning is expected if the server doesn't send proper headers
        try {
          const source = audioContext.createMediaElementSource(audioElement)

          // Create gain node (controls volume only)
          const gain = audioContext.createGain()
          gain.gain.value = 1 // keep signal alive
          gainRef.current = gain

          // ANALYSIS PATH (never muted)
          source.connect(analyser)

          // PLAYBACK PATH (can be muted)
          source.connect(gain)
          gain.connect(audioContext.destination)

          sourceRef.current = source

          if (gainRef.current && mute) {
            gainRef.current.gain.value = 0
          }
        } catch (sourceError) {
          console.warn('Could not create MediaElementAudioSource (CORS issue). Audio will still play, but analyzer may output zeros.')
          // Clean up and return - don't prevent playback
          isSetupRef.current = false
          audioContextRef.current = null
          analyserRef.current = null
          return
        }

        // Start analyzing
        const frequencyData = new Float32Array(analyser.frequencyBinCount)

        const analyze = (): void => {
          if (!analyserRef.current) return

          analyserRef.current.getFloatFrequencyData(frequencyData)

          // Calculate bass (low frequencies), mid, treble
          const bassEnd = Math.floor(frequencyData.length * 0.1)
          const midEnd = Math.floor(frequencyData.length * 0.5)

          let bassSum = 0
          let midSum = 0
          let trebleSum = 0

          for (let i = 0; i < bassEnd; i++) {
            bassSum += (frequencyData[i] + 140) / 140 // Normalize
          }
          for (let i = bassEnd; i < midEnd; i++) {
            midSum += (frequencyData[i] + 140) / 140
          }
          for (let i = midEnd; i < frequencyData.length; i++) {
            trebleSum += (frequencyData[i] + 140) / 140
          }

          const bass = Math.max(0, bassSum / bassEnd)
          const mid = Math.max(0, midSum / (midEnd - bassEnd))
          const treble = Math.max(0, trebleSum / (frequencyData.length - midEnd))
          const overall = (bass + mid + treble) / 3

          setAudioData({
            frequencies: frequencyData,
            bass,
            mid,
            treble,
            overall
          })

          animationFrameRef.current = requestAnimationFrame(analyze)
        }

        isSetupRef.current = true
        analyze()
      } catch (error) {
        console.error('Error setting up audio analyzer:', error)
        isSetupRef.current = false
      }
    }

    // Resume audio context when audio plays (required by browsers)
    const handlePlay = async () => {
      if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
        try {
          await audioContextRef.current.resume()
        } catch (error) {
          console.error('Error resuming audio context:', error)
        }
      }
    }

    // Set up when audio is ready
    if (audioElement.readyState >= 2) {
      // Audio is already loaded
      setupAudio()
    } else {
      // Wait for audio to load
      audioElement.addEventListener('loadeddata', setupAudio, { once: true })
    }

    // Listen for play events to resume context
    audioElement.addEventListener('play', handlePlay)

    // Cleanup function
    return () => {
      audioElement.removeEventListener('play', handlePlay)
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
      // Don't close the audio context here as it might interfere with playback
      // The browser will handle cleanup when the audio element is removed
    }
  }, [audioRef])

  return audioData
}

